import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface InvoicePDFData {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  client: {
    name: string;
    email: string;
    phone: string;
    company: string;
    address?: string;
  };
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  terms?: string;
}

export const generateInvoicePDF = async (data: InvoicePDFData, currencySymbol: string = 'R'): Promise<jsPDF> => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 25;
  const contentWidth = pageWidth - (margin * 2);

  // Modern color scheme
  const primaryColor = [59, 130, 246]; // Blue
  const secondaryColor = [107, 114, 128]; // Gray
  const accentColor = [34, 197, 94]; // Green
  const dangerColor = [239, 68, 68]; // Red
  const lightGray = [249, 250, 251];
  const borderColor = [229, 231, 235];

  // Header with modern design
  pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  pdf.rect(0, 0, pageWidth, 45, 'F');
  
  // Company branding
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.text('C-Band', margin, 28);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Professional Invoicing & Accounting', margin, 38);

  // Invoice title (right side)
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('INVOICE', pageWidth - margin - 40, 28);
  
  // Reset text color for content
  pdf.setTextColor(0, 0, 0);

  // Invoice details box
  const detailsBoxY = 55;
  const detailsBoxWidth = 80;
  const detailsBoxHeight = 35;
  
  pdf.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
  pdf.roundedRect(pageWidth - margin - detailsBoxWidth, detailsBoxY, detailsBoxWidth, detailsBoxHeight, 3, 3, 'F');
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.roundedRect(pageWidth - margin - detailsBoxWidth, detailsBoxY, detailsBoxWidth, detailsBoxHeight, 3, 3, 'S');
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Invoice Details', pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 8);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(`#${data.invoiceNumber}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 18);
  pdf.text(`Issue: ${new Date(data.issueDate).toLocaleDateString()}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 25);
  pdf.text(`Due: ${new Date(data.dueDate).toLocaleDateString()}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 32);

  // Client information section
  const clientY = 55;
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Bill To:', margin, clientY);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.client.name, margin, clientY + 12);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  if (data.client.company) {
    pdf.text(data.client.company, margin, clientY + 20);
  }
  if (data.client.address) {
    pdf.text(data.client.address, margin, clientY + 28);
  }
  pdf.text(data.client.email, margin, clientY + 36);
  pdf.text(data.client.phone, margin, clientY + 44);

  // Items table with modern styling
  const tableY = 120;
  const colWidths = [85, 25, 25, 25, 25];
  const colX = [margin, margin + 85, margin + 110, margin + 135, margin + 160];

  // Table header with modern design
  pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  pdf.roundedRect(margin, tableY - 12, contentWidth, 12, 2, 2, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Description', colX[0] + 2, tableY - 4);
  pdf.text('Qty', colX[1] + 2, tableY - 4);
  pdf.text('Price', colX[2] + 2, tableY - 4);
  pdf.text('Total', colX[3] + 2, tableY - 4);

  // Table rows with alternating colors
  pdf.setTextColor(0, 0, 0);
  let currentY = tableY + 5;

  data.items.forEach((item, index) => {
    if (currentY > pageHeight - 100) {
      pdf.addPage();
      currentY = 30;
    }

    // Alternating row colors
    if (index % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, currentY - 3, contentWidth, 10, 'F');
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    
    // Description with text wrapping
    const description = item.description;
    const maxWidth = 80;
    const lines = pdf.splitTextToSize(description, maxWidth);
    
    lines.forEach((line: string, lineIndex: number) => {
      pdf.text(line, colX[0] + 2, currentY + (lineIndex * 3));
    });
    
    const descriptionHeight = Math.max(lines.length * 3, 8);
    
    // Other columns
    pdf.text(item.quantity.toString(), colX[1] + 2, currentY + 2);
    pdf.text(`${currencySymbol}${item.unitPrice.toFixed(2)}`, colX[2] + 2, currentY + 2);
    pdf.text(`${currencySymbol}${item.total.toFixed(2)}`, colX[3] + 2, currentY + 2);
    
    currentY += descriptionHeight + 2;
    
    // Add subtle separator line
    if (index < data.items.length - 1) {
      pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 2;
    }
  });

  // Totals section with modern design
  const totalsY = currentY + 15;
  const totalsBoxWidth = 100;
  const totalsBoxHeight = 60;
  
  pdf.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
  pdf.roundedRect(pageWidth - margin - totalsBoxWidth, totalsY, totalsBoxWidth, totalsBoxHeight, 3, 3, 'F');
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.roundedRect(pageWidth - margin - totalsBoxWidth, totalsY, totalsBoxWidth, totalsBoxHeight, 3, 3, 'S');
  
  const totalsX = pageWidth - margin - totalsBoxWidth + 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Subtotal:', totalsX, totalsY + 15);
  pdf.text(`${currencySymbol}${data.subtotal.toFixed(2)}`, pageWidth - margin - 10, totalsY + 15);
  
  pdf.text('Tax (10%):', totalsX, totalsY + 25);
  pdf.text(`${currencySymbol}${data.tax.toFixed(2)}`, pageWidth - margin - 10, totalsY + 25);
  
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.line(totalsX, totalsY + 30, pageWidth - margin - 10, totalsY + 30);
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text('Total:', totalsX, totalsY + 45);
  pdf.text(`${currencySymbol}${data.total.toFixed(2)}`, pageWidth - margin - 10, totalsY + 45);

  // Notes and Terms section
  if (data.notes || data.terms) {
    const notesY = totalsY + totalsBoxHeight + 20;
    
    if (data.notes) {
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Notes:', margin, notesY);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const notesLines = pdf.splitTextToSize(data.notes, contentWidth - 20);
      notesLines.forEach((line: string, index: number) => {
        pdf.text(line, margin + 5, notesY + 8 + (index * 4));
      });
    }

    if (data.terms) {
      const termsY = notesY + (data.notes ? 25 : 0);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Terms & Conditions:', margin, termsY);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const termsLines = pdf.splitTextToSize(data.terms, contentWidth - 20);
      termsLines.forEach((line: string, index: number) => {
        pdf.text(line, margin + 5, termsY + 8 + (index * 4));
      });
    }
  }

  // Modern footer
  const footerY = pageHeight - 25;
  pdf.setFontSize(9);
  pdf.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
  pdf.setFont('helvetica', 'normal');
  
  // Footer line
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
  
  pdf.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });
  pdf.setFontSize(8);
  pdf.text('C-Band - Professional Invoicing & Accounting System', pageWidth / 2, footerY + 5, { align: 'center' });
  pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, footerY + 10, { align: 'center' });

  return pdf;
};

export const downloadInvoicePDF = async (data: InvoicePDFData, filename?: string, currencySymbol?: string): Promise<void> => {
  try {
    const pdf = await generateInvoicePDF(data, currencySymbol);
    const defaultFilename = `invoice-${data.invoiceNumber}-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename || defaultFilename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF');
  }
};
